import { useEffect, useRef, useState } from "react";

const contactInfo = [
  {
    icon: "ri-map-pin-2-line",
    label: "Address",
    lines: [
      "STR. ION MAIORESCU NR 12",
      "BLOC 33S1 ETAJ 7 APARTAMENT 7A",
      "Ploiesti, 100001",
      "Romania",
    ],
  },
  {
    icon: "ri-mail-line",
    label: "Email",
    lines: ["info@sudarcsrl.com"],
  },
  {
    icon: "ri-phone-line",
    label: "Phone",
    lines: ["+1 6626856779"],
  },
];

export default function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const message = data.get("message") as string;
    if (message && message.length > 500) return;

    setFormStatus("sending");
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === "string") params.append(key, value);
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d77pcut5uau42b5indd0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <section id="contact" style={{ background: "#111318" }}>
      {/* Top gradient fade */}
      <div className="h-12 w-full" style={{ background: "linear-gradient(to bottom, #f7f8fb, #111318)" }} />

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28" ref={sectionRef}>
        {/* Header */}
        <div
          className="mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}
        >
          <span className="text-xs font-semibold tracking-widest uppercase mb-4 block" style={{ color: "#7de8c8" }}>
            Get In Touch
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Contact Us
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-16">
          {/* Left: contact info */}
          <div
            className="flex-1 flex flex-col gap-10"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(-30px)",
              transition: "opacity 0.7s ease 0.15s, transform 0.7s ease 0.15s",
            }}
          >
            {contactInfo.map((item) => (
              <div key={item.label} className="flex gap-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: "rgba(125,232,200,0.12)" }}>
                  <i className={`${item.icon} text-lg`} style={{ color: "#7de8c8" }} />
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: "#7de8c8" }}>
                    {item.label}
                  </p>
                  {item.lines.map((line) => (
                    <p key={line} className="text-sm leading-relaxed" style={{ color: "#b3b3b3" }}>
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            ))}

            {/* Company info */}
            <div className="mt-4 pt-10 border-t" style={{ borderColor: "#222530" }}>
              <p className="text-2xl font-bold text-white mb-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.1em" }}>
                SUDARC SRL
              </p>
              <p className="text-sm" style={{ color: "#666" }}>
                Modern iOS Applications &mdash; Security &amp; Performance
              </p>
            </div>
          </div>

          {/* Right: contact form */}
          <div
            className="flex-1"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(30px)",
              transition: "opacity 0.7s ease 0.25s, transform 0.7s ease 0.25s",
            }}
          >
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div className="flex flex-col sm:flex-row gap-5">
                <div className="flex-1 flex flex-col gap-2">
                  <label className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#666" }}>
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Your name"
                    className="w-full rounded-xl px-4 py-3 text-sm text-white outline-none focus:ring-1 transition-all"
                    style={{
                      background: "#1a1d26",
                      border: "1px solid #2a2d3a",
                      color: "#fff",
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "#7de8c8")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "#2a2d3a")}
                  />
                </div>
                <div className="flex-1 flex flex-col gap-2">
                  <label className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#666" }}>
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@email.com"
                    className="w-full rounded-xl px-4 py-3 text-sm text-white outline-none transition-all"
                    style={{
                      background: "#1a1d26",
                      border: "1px solid #2a2d3a",
                      color: "#fff",
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "#7de8c8")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "#2a2d3a")}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#666" }}>
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  required
                  placeholder="How can we help?"
                  className="w-full rounded-xl px-4 py-3 text-sm text-white outline-none transition-all"
                  style={{
                    background: "#1a1d26",
                    border: "1px solid #2a2d3a",
                    color: "#fff",
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#7de8c8")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "#2a2d3a")}
                />
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#666" }}>
                    Message
                  </label>
                  <span className="text-xs" style={{ color: charCount > 500 ? "#ff6b6b" : "#555" }}>
                    {charCount}/500
                  </span>
                </div>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Tell us about your inquiry..."
                  className="w-full rounded-xl px-4 py-3 text-sm text-white outline-none transition-all resize-none"
                  style={{
                    background: "#1a1d26",
                    border: "1px solid #2a2d3a",
                    color: "#fff",
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#7de8c8")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "#2a2d3a")}
                  onChange={(e) => setCharCount(e.target.value.length)}
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === "sending" || charCount > 500}
                className="w-full rounded-xl py-4 text-sm font-semibold tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
                style={{
                  background: formStatus === "success" ? "#00b894" : "linear-gradient(135deg, #0f3460 0%, #00b894 100%)",
                  color: "#fff",
                }}
              >
                {formStatus === "sending" ? "Sending..." : formStatus === "success" ? "Message Sent!" : "Send Message"}
              </button>

              {formStatus === "error" && (
                <p className="text-sm text-center" style={{ color: "#ff6b6b" }}>
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Footer bar */}
      <div className="border-t" style={{ borderColor: "#1e2130" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs" style={{ color: "#444" }}>
            &copy; {new Date().getFullYear()} SUDARC SRL. All rights reserved.
          </p>
          <p className="text-xs" style={{ color: "#444" }}>
            iOS App Development &mdash; Ploiesti, Romania
          </p>
        </div>
      </div>
    </section>
  );
}
